import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { DOC_TYPES, docTypeLabel } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, X, Sparkle, CaretRight, Buildings, ShieldCheck, FileText, ListChecks, CheckCircle } from "@phosphor-icons/react";

const STEPS = ["Requirement", "Applicability", "Policy", "Procedure", "Control"];
const FREQ = ["Continuous", "Daily", "Weekly", "Monthly", "Quarterly", "Semi-Annual", "Annual"];
const PROC_DOC_TYPES = DOC_TYPES.filter((d) => d.value !== "policy");

export default function BuildControls() {
  const { aiEnabled } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [standards, setStandards] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [profile, setProfile] = useState(null);

  const [selStd, setSelStd] = useState("");
  const [selReq, setSelReq] = useState(params.get("req") || "");
  const [applic, setApplic] = useState(null);
  const [newQ, setNewQ] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  const [policyMode, setPolicyMode] = useState("ai");
  const [policy, setPolicy] = useState(null); // {content, applicability_status, reason, title, parts_used}
  const [genBusy, setGenBusy] = useState(false);
  const [policyId, setPolicyId] = useState(null);

  const [procType, setProcType] = useState("procedure");
  const [procSections, setProcSections] = useState([]);
  const [procTitle, setProcTitle] = useState("");
  const [procId, setProcId] = useState(null);

  const [control, setControl] = useState({ name: "", objective: "", owner: "", frequency: "Annual" });
  const [controlId, setControlId] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/standards"), api.get("/requirements"), api.get("/entity-profile")]).then(([s, r, p]) => {
      setStandards(s.data); setReqs(r.data); setProfile(p.data);
    });
  }, []);

  const req = reqs.find((r) => r.id === selReq);
  const reqOptions = useMemo(() => reqs.filter((r) => r.standard_id === selStd), [reqs, selStd]);
  useEffect(() => { if (req) setSelStd(req.standard_id); }, [req]);

  const loadApplic = useCallback(() => {
    if (!selReq) return;
    api.get(`/requirements/${selReq}/applicability`).then((r) => setApplic(r.data));
  }, [selReq]);
  useEffect(() => { loadApplic(); }, [loadApplic]);

  // ---- Applicability ----
  const answerQ = async (qid, val) => {
    await api.patch(`/applicability/${qid}`, { answer: val });
    loadApplic();
  };
  const setGating = async (qid, val) => {
    await api.patch(`/applicability/${qid}`, { exclude_if: val === "never" ? null : val });
    loadApplic();
  };
  const addQuestion = async (text, help = "") => {
    if (!text.trim() || !req) return;
    await api.post("/applicability", { standard_id: req.standard_id, requirement_id: null, text, help });
    setNewQ(""); loadApplic();
  };
  const suggestQuestions = async () => {
    if (!req) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/ai/suggest-applicability/${req.standard_id}`);
      for (const s of data.suggestions) await api.post("/applicability", { standard_id: req.standard_id, requirement_id: null, text: s.text, help: s.help || "", exclude_if: s.exclude_if ?? null });
      loadApplic(); toast.success(`Added ${data.suggestions.length} AI-suggested questions`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };

  // ---- Policy ----
  const genPolicy = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/policy/generate/${selReq}`, { mode: policyMode });
      setPolicy(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenBusy(false); }
  };
  const savePolicy = async () => {
    try {
      const { data } = await api.post("/documents", {
        title: policy.title, doc_type: "policy", linked_requirements: [selReq],
        sections: [{ id: crypto.randomUUID(), title: "Policy Statement", content: policy.content }],
        applicability_status: policy.applicability_status, applicability_reason: policy.reason,
      });
      setPolicyId(data.id);
      toast.success("Policy saved");
      setProcTitle(`${req.standard_id} ${req.req_number} ${docTypeLabel(procType)}`);
      setStep(4);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Procedure ----
  const genProcedure = async () => {
    setGenBusy(true);
    try {
      const data = await runAiJob(`/ai/draft-procedure/${selReq}`, { policy_id: policyId, doc_type: procType });
      setProcSections(data.sections);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenBusy(false); }
  };
  const setSection = (i, v) => { const s = [...procSections]; s[i] = { ...s[i], content: v }; setProcSections(s); };
  const saveProcedure = async () => {
    try {
      const sections = procSections.length ? procSections : [{ id: crypto.randomUUID(), title: "Procedure", content: "" }];
      const { data } = await api.post("/documents", {
        title: procTitle || `${req.standard_id} ${req.req_number} ${docTypeLabel(procType)}`,
        doc_type: procType, linked_requirements: [selReq], guided_by: policyId ? [policyId] : [], sections,
      });
      setProcId(data.id);
      setControl({ name: `${req.standard_id} ${req.req_number} Control`, objective: `Operationalize ${req.standard_id} ${req.req_number} per the governing policy and ${docTypeLabel(procType).toLowerCase()}.`, owner: profile?.name || "", frequency: "Annual" });
      toast.success(`${docTypeLabel(procType)} saved`);
      setStep(5);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Control ----
  const createControl = async () => {
    if (!control.name.trim()) return toast.error("Control name required");
    try {
      const docs = [policyId, procId].filter(Boolean);
      const { data } = await api.post("/controls", { ...control, linked_requirements: [selReq], linked_documents: docs });
      setControlId(data.id);
      toast.success("Control created — the chain is complete!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const standardOf = (sid) => standards.find((s) => s.standard_id === sid);

  return (
    <div>
      <PageHeader title="Build the Ps" subtitle="Requirement → Applicability → Policy → Procedure/Plan → Control" />
      <div className="px-8 pt-6">
        <div className="flex items-center gap-1 mb-6" data-testid="wizard-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <button onClick={() => i + 1 <= step && setStep(i + 1)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border ${step === i + 1 ? "bg-primary text-primary-foreground border-black" : i + 1 < step ? "bg-secondary border-border" : "border-border text-muted-foreground"}`}>
                <span className="font-mono">{i + 1}</span>{s}
              </button>
              {i < STEPS.length - 1 && <CaretRight size={14} className="text-muted-foreground mx-0.5" />}
            </div>
          ))}
        </div>
      </div>

      <div className="px-8 pb-12 max-w-4xl">
        {/* STEP 1 — Requirement */}
        {step === 1 && (
          <div className="border border-border bg-card p-6 space-y-4" data-testid="step-requirement">
            <div className="flex items-center gap-2 font-display font-bold text-lg"><ListChecks size={20} />Select a NERC Requirement</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Standard</Label>
                <Select value={selStd} onValueChange={(v) => { setSelStd(v); setSelReq(""); }}>
                  <SelectTrigger data-testid="wiz-standard" className="rounded-none mt-1"><SelectValue placeholder="Choose standard" /></SelectTrigger>
                  <SelectContent>{standards.map((s) => <SelectItem key={s.id} value={s.standard_id}>{s.standard_id} — {s.title?.slice(0, 40)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Requirement</Label>
                <Select value={selReq} onValueChange={setSelReq} disabled={!selStd}>
                  <SelectTrigger data-testid="wiz-requirement" className="rounded-none mt-1"><SelectValue placeholder="Choose requirement" /></SelectTrigger>
                  <SelectContent>{reqOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.req_number} — {r.text?.slice(0, 50)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {req && <div className="border border-border p-3 bg-secondary/30 text-sm"><span className="font-mono font-bold">{req.standard_id} {req.req_number}</span><p className="mt-1">{req.text}</p></div>}
            {!profile?.registered_functions?.length && (
              <div className="border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300 text-sm px-3 py-2 flex items-center gap-2">
                <Buildings size={16} />No registered functions set. <button className="underline" onClick={() => nav("/admin")}>Set your Entity Profile</button> for accurate applicability.
              </div>
            )}
            <Button data-testid="step1-next" disabled={!selReq} onClick={() => setStep(2)} className="rounded-none bg-primary hover:bg-primary/90">Continue<CaretRight size={16} className="ml-1" /></Button>
          </div>
        )}

        {/* STEP 2 — Applicability */}
        {step === 2 && applic && (
          <div className="space-y-4" data-testid="step-applicability">
            <div className={`border p-5 ${applic.applicable ? "border-green-300 bg-green-50 dark:border-green-500/40 dark:bg-green-500/10" : "border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10"}`}>
              <div className="flex items-center gap-2 font-display font-bold text-lg">
                {applic.applicable ? <Check weight="bold" className="text-green-700 dark:text-green-400" /> : <X weight="bold" className="text-red-700 dark:text-red-400" />}
                {applic.applicable ? "Applicable" : "Not Applicable"} to {profile?.name || "the entity"}
              </div>
              <div className="text-sm mt-2 grid md:grid-cols-2 gap-2">
                <div><span className="overline text-muted-foreground">Your functions</span><div className="font-mono">{applic.registered_functions.join(", ") || "none set"}</div></div>
                <div><span className="overline text-muted-foreground">Requirement applies to</span><div className="font-mono">{applic.requirement_functions.join(", ") || "unspecified (all)"}</div></div>
              </div>
              {applic.reasons.length > 0 && <ul className="list-disc pl-5 text-sm mt-2 text-red-700">{applic.reasons.map((r, i) => <li key={`${i}-${r.slice(0, 24)}`}>{r}</li>)}</ul>}
              {applic.applicable_parts.length > 0 && (
                <div className="mt-3 text-sm"><span className="overline text-muted-foreground">Applicable parts for your function(s)</span>
                  <div className="mt-1 space-y-1">{applic.applicable_parts.map((p) => <div key={p.label}><span className="font-mono font-bold">{p.label}</span> {p.text?.slice(0, 90)}</div>)}</div>
                </div>
              )}
            </div>

            <div className="border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-bold">Qualifying Applicability Questions</span>
                <Button data-testid="ai-suggest-questions" onClick={suggestQuestions} disabled={suggesting || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined} variant="outline" size="sm" className="rounded-none"><Sparkle size={14} weight="fill" className="mr-1" />{suggesting ? "Thinking…" : "AI suggest"}</Button>
              </div>
              <div className="space-y-2">
                {applic.questions.map((q) => {
                  const answered = q.answer === true || q.answer === false;
                  const excludes = answered && q.exclude_if && (q.answer ? "yes" : "no") === q.exclude_if;
                  return (
                    <div key={q.id} data-testid={`q-${q.id}`} className={`border p-3 ${excludes ? "border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10" : "border-border"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm"><div>{q.text}</div>{q.help && <div className="text-xs text-muted-foreground">{q.help}</div>}</div>
                        <div className="flex gap-1 shrink-0">
                          <button data-testid={`q-${q.id}-yes`} onClick={() => answerQ(q.id, true)} className={`border px-2 py-1 text-xs font-semibold ${q.answer === true ? "bg-green-600 text-white border-green-600" : "border-border hover:bg-secondary"}`}>Yes</button>
                          <button data-testid={`q-${q.id}-no`} onClick={() => answerQ(q.id, false)} className={`border px-2 py-1 text-xs font-semibold ${q.answer === false ? "bg-red-600 text-white border-red-600" : "border-border hover:bg-secondary"}`}>No</button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-border/60">
                        <span className="text-xs text-muted-foreground">Marks requirement Not Applicable if answered:</span>
                        <Select value={q.exclude_if || "never"} onValueChange={(v) => setGating(q.id, v)}>
                          <SelectTrigger data-testid={`q-${q.id}-gate`} className="rounded-none h-7 w-[180px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no">No → Not Applicable</SelectItem>
                            <SelectItem value="yes">Yes → Not Applicable</SelectItem>
                            <SelectItem value="never">Never (informational)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {excludes && <div className="text-xs text-red-700 font-semibold mt-1">→ This answer marks the requirement Not Applicable.</div>}
                    </div>
                  );
                })}
                {applic.questions.length === 0 && <p className="text-sm text-muted-foreground">No applicability questions yet. Add one or use AI suggest.</p>}
              </div>
              <div className="flex gap-2 mt-3">
                <Input data-testid="new-question-input" value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="e.g. Do you own transmission lines ≥ 1 mile at 200kV+?" className="rounded-none" />
                <Button data-testid="add-question-btn" onClick={() => addQuestion(newQ)} variant="outline" className="rounded-none">Add</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">A question only marks the requirement Not Applicable when answered with its gating answer (set per question above). Set scoping/reverse-worded questions to "Never (informational)" — e.g. CIP-002 still applies even if you answer "No" to some.</p>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" className="rounded-none" onClick={() => setStep(1)}>Back</Button>
              <Button data-testid="step2-next" onClick={() => setStep(3)} className="rounded-none bg-primary hover:bg-primary/90">Continue to Policy<CaretRight size={16} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Policy */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-policy">
            <div className="border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-display font-bold text-lg"><FileText size={20} />Policy ("The {profile?.name || "Entity"} shall…")</div>
                <div className="flex items-center gap-2">
                  <Select value={policyMode} onValueChange={setPolicyMode}>
                    <SelectTrigger data-testid="policy-mode" className="rounded-none h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ai">AI-drafted</SelectItem><SelectItem value="template">Template</SelectItem></SelectContent>
                  </Select>
                  <Button data-testid="generate-policy-btn" onClick={genPolicy} disabled={genBusy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined} className="rounded-none bg-primary hover:bg-primary/90"><Sparkle size={14} weight="fill" className="mr-1" />{genBusy ? "Generating…" : "Generate"}</Button>
                </div>
              </div>
              {policy && (
                <div className="mt-4 space-y-2">
                  <div className={`inline-block border px-2 py-0.5 text-[11px] font-semibold uppercase ${policy.applicable ? "bg-green-50 text-green-700 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40" : "bg-red-50 text-red-700 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/40"}`}>{policy.applicability_status?.replace("_", " ")}</div>
                  <Input data-testid="policy-title" value={policy.title} onChange={(e) => setPolicy({ ...policy, title: e.target.value })} className="rounded-none font-semibold" />
                  <Textarea data-testid="policy-content" value={policy.content} onChange={(e) => setPolicy({ ...policy, content: e.target.value })} className="rounded-none min-h-[260px] font-mono text-sm" />
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" className="rounded-none" onClick={() => setStep(2)}>Back</Button>
              <Button data-testid="save-policy-btn" disabled={!policy} onClick={savePolicy} className="rounded-none bg-primary hover:bg-primary/90">Save Policy & Continue<CaretRight size={16} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {/* STEP 4 — Procedure / Plan / Program / Process */}
        {step === 4 && (
          <div className="space-y-4" data-testid="step-procedure">
            <div className="border border-border bg-card p-5 space-y-3">
              <div className="flex items-center gap-2 font-display font-bold text-lg"><ShieldCheck size={20} />Implementing Document (guided by the Policy)</div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type</Label>
                  <Select value={procType} onValueChange={(v) => { setProcType(v); setProcTitle(`${req.standard_id} ${req.req_number} ${docTypeLabel(v)}`); }}>
                    <SelectTrigger data-testid="proc-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROC_DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input data-testid="proc-title" value={procTitle} onChange={(e) => setProcTitle(e.target.value)} className="rounded-none mt-1" /></div>
              </div>
              <Button data-testid="generate-procedure-btn" onClick={genProcedure} disabled={genBusy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined} variant="outline" className="rounded-none"><Sparkle size={14} weight="fill" className="mr-1" />{genBusy ? "Drafting…" : `AI draft ${docTypeLabel(procType)}`}</Button>
              <div className="space-y-3">
                {procSections.map((s, i) => (
                  <div key={s.id || i} className="border border-border">
                    <div className="border-b border-border px-3 py-2 bg-secondary/40 font-semibold text-sm">{s.title}</div>
                    <Textarea data-testid={`proc-section-${i}`} value={s.content} onChange={(e) => setSection(i, e.target.value)} className="rounded-none border-0 min-h-[100px] font-mono text-sm" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" className="rounded-none" onClick={() => setStep(3)}>Back</Button>
              <Button data-testid="save-procedure-btn" onClick={saveProcedure} className="rounded-none bg-primary hover:bg-primary/90">Save {docTypeLabel(procType)} & Continue<CaretRight size={16} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {/* STEP 5 — Control */}
        {step === 5 && (
          <div className="space-y-4" data-testid="step-control">
            {!controlId ? (
              <div className="border border-border bg-card p-5 space-y-3">
                <div className="flex items-center gap-2 font-display font-bold text-lg"><ShieldCheck size={20} />Create the Control</div>
                <div><Label>Control Name</Label><Input data-testid="control-name" value={control.name} onChange={(e) => setControl({ ...control, name: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label>Objective</Label><Textarea value={control.objective} onChange={(e) => setControl({ ...control, objective: e.target.value })} className="rounded-none mt-1" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Owner</Label><Input value={control.owner} onChange={(e) => setControl({ ...control, owner: e.target.value })} className="rounded-none mt-1" /></div>
                  <div><Label>Frequency</Label>
                    <Select value={control.frequency} onValueChange={(v) => setControl({ ...control, frequency: v })}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" className="rounded-none" onClick={() => setStep(4)}>Back</Button>
                  <Button data-testid="create-control-btn" onClick={createControl} className="rounded-none bg-primary hover:bg-primary/90">Create Control</Button>
                </div>
              </div>
            ) : (
              <div className="border border-green-300 bg-green-50 dark:border-green-500/40 dark:bg-green-500/10 p-6 text-center" data-testid="build-complete">
                <CheckCircle size={40} weight="fill" className="text-green-600 dark:text-green-400 mx-auto" />
                <div className="font-display font-black text-2xl mt-2">Control Chain Complete</div>
                <p className="text-sm text-muted-foreground mt-1">{req.standard_id} {req.req_number}: Policy → {docTypeLabel(procType)} → Control are linked.</p>
                <div className="flex justify-center gap-2 mt-4">
                  <Button onClick={() => nav(`/requirements/${selReq}`)} variant="outline" className="rounded-none">View Requirement</Button>
                  <Button onClick={() => nav("/controls")} variant="outline" className="rounded-none">View Controls</Button>
                  <Button data-testid="build-another" onClick={() => { setStep(1); setSelReq(""); setApplic(null); setPolicy(null); setPolicyId(null); setProcSections([]); setProcId(null); setControlId(null); }} className="rounded-none bg-primary hover:bg-primary/90">Build Another</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
